/**
 * Normalization layer: Aave V4 Hub & Spoke → multi-market lender response.
 *
 * ## Architecture
 *
 * Aave V4 Spokes are **isolated risk silos**: each Spoke has its own
 * collateral configs, its own user health factor, its own risk premium, and
 * shares no solvency with other Spokes (the Hub is the shared liquidity
 * layer, not a shared risk surface). The architectural analogues:
 *
 *   Aave v3 (legacy)   →  one giant shared pool      (one lender key)
 *   Compound v3        →  one Comet per base asset   (one lender key per Comet)
 *   Morpho Blue        →  one market per pair        (one lender key per marketId)
 *   Aave v4            →  one Spoke per risk silo    (one lender key per spoke)  ← THIS
 *
 * To match this reality and treat V4 like any other isolated-market protocol,
 * the V4 normalizer is a **multi-market lender** (`isMultiMarket()` returns
 * true). Each Spoke gets its own synthesized lender key:
 *
 *   `${baseHubLender}_${spokeAddr.slice(2).toUpperCase()}`
 *
 * e.g. `AAVE_V4_CORE_94E7A5DCBE816E498B89AB752661904E2F56C485`. The base Hub
 * lender (`AAVE_V4_CORE`, `AAVE_V4_PLUS`, `AAVE_V4_PRIME`) is used only as
 * the **input/discovery** identifier passed to the fetcher. The output is
 * keyed exclusively by per-spoke synthesized keys.
 *
 * The dispatcher in `fetchLender.ts` `Object.assign`s the multi-market map
 * into the top-level lender data, so callers see V4 spokes the same way
 * they see Morpho markets — flat, top-level, one entry per isolated market.
 */

import type { ParsedSpokeData } from './types'
import { BPS } from '../../../../utils/parsing'
import {
  aaveV4SpokeLenderKey,
  parseAaveV4SpokeLenderKey,
} from '@1delta/data-sdk'

// ---- Target format types (matching existing convention) ----

/**
 * Per-config entry shape. Mirrors `ConfigEntry` in `types/apiReturnType.ts`
 * — `category` is a `number` to match Aave V3 (e-mode index). For V4 the
 * value is the on-chain `dynamicConfigKey` (also an integer).
 */
export interface LenderConfigData {
  category: number
  label: string
  borrowCollateralFactor: number
  collateralFactor: number
  borrowFactor: number
  liquidationPenalty?: number
  closeFactor?: number
  targetHealthFactor?: number
  collateralDisabled?: boolean
  debtDisabled?: boolean
}

/**
 * Per-marketUid identity inside an Aave V4 spoke. The (hub, assetId,
 * reserveId) triple locates the asset in the on-chain Hub & Spoke binding
 * for action routing — they're not derivable from the marketUid alone
 * (which only encodes the underlying address).
 */
export interface AaveV4MarketMetadata {
  /** Hub-internal asset ID for this underlying (scoped to `hub`) */
  assetId: number
  /** Spoke-internal reserve ID for this underlying in this spoke */
  reserveId: number
  /**
   * Per-reserve hub address (lowercase). A single spoke can mix reserves
   * across hubs; this is the hub THIS reserve binds to.
   */
  hub: string
}

/**
 * Lender-key-wide params for a single Aave V4 spoke. Constant across every
 * marketUid in the same per-spoke entry — analogous to Morpho's market-level
 * params (loan token, collateral token, oracle, irm). Lives at the top level
 * of the multi-market response wrapper, not on individual marketUid entries.
 *
 * Note: there is **no** spoke-level `hub` here. Hub identity is per-reserve;
 * see `AaveV4MarketMetadata.hub`.
 */
export interface AaveV4SpokeParams {
  /** This entry's spoke address (lowercase) */
  spoke: string
  /** Spoke's oracle address (lowercase) */
  oracle: string
  /** Spoke label from data-sdk metadata (e.g. "Core / Default") */
  label: string
  /**
   * UI/grouping hint only — `AAVE_V4_CORE` / `AAVE_V4_PLUS` / `AAVE_V4_PRIME`.
   * NOT used for routing or lender keying.
   */
  baseHubAttribution?: string
  /**
   * Spoke-level `getLiquidationConfig()`, verbatim in human units.
   *
   * `targetHealthFactor` is mirrored onto every config entry because that is
   * the cross-lender column; the other two have no column and live only here.
   * They are what make the published `liquidationPenalty` legible: it is
   * `maxLiquidationBonus`, reached only at `healthFactorForMaxBonus`, with
   * `liquidationBonusFactor` interpolating between there and HF 1 — so without
   * them the max reads as a flat penalty every liquidation charges.
   */
  liquidation?: {
    /** WAD → e.g. 1.24. Liquidations are sized to restore this HF. */
    targetHealthFactor?: number
    /** WAD → e.g. 0.95. At/below this the bonus is `maxLiquidationBonus`. */
    healthFactorForMaxBonus?: number
    /** BPS, verbatim — the interpolation factor for the bonus. */
    liquidationBonusFactorBps?: number
  }
}

export interface AaveV4TokenEntry {
  marketUid: string
  name: string
  poolId: string
  underlying: string
  asset: any
  /**
   * Spoke-local supply (from Spoke.getReserveSuppliedAssets) as decimal
   * string. Aggregatable: `sum(totalDeposits)` across spokes gives real TVL
   * without double-counting the shared hub pool.
   */
  totalDeposits: string
  totalDebtStable: string
  /**
   * Spoke-local total debt (drawn + premium) as decimal string.
   * Aggregatable across spokes for TVL: `TVL = Σ deposits − Σ debts`.
   */
  totalDebt: string
  /**
   * Pool liquidity: hub-level `asset.liquidity` — the raw amount available
   * in the hub for this asset. This is the withdraw-side liquidity ceiling
   * (not gated by spoke caps). Matches the semantics of `totalLiquidity`
   * across all other protocols (`deposits − debt`).
   */
  totalLiquidity: number
  totalDepositsUSD: number
  totalDebtStableUSD: number
  totalDebtUSD: number
  totalLiquidityUSD: number
  /**
   * Hub-level total supply across all spokes as decimal string.
   * This is the IRM-relevant denominator (`hubTotalOwed / hubDeposits`).
   */
  hubDeposits: string
  /** Hub-level total owed across all spokes as decimal string. */
  hubBorrows: string
  hubDepositsUSD: number
  hubBorrowsUSD: number
  depositRate: number
  variableBorrowRate: number
  stableBorrowRate: number
  /**
   * Hub-level borrow utilization (0..1) — the exact IRM input
   * `drawn / (liquidity + drawn + swept)` (premium debt excluded, deficit
   * ignored — mirrors the hub's AssetInterestRateStrategy). The V4 IRM
   * lives on the hub and aggregates drawn liquidity across all spokes
   * sharing this asset, so this is the IRM-relevant utilization, NOT the
   * per-spoke ratio. Per-spoke `(suppliedAssets/totalDebt)` would be
   * misleading here (e.g. always negative for borrow-only spokes).
   */
  utilization: number
  /**
   * Totals in the hub IRM's utilization basis (token units):
   * `irmTotalDeposits = liquidity + drawn + swept`, `irmTotalDebt = drawn`.
   * Rate-impact simulations must shift these, not the spoke-local totals.
   */
  irmTotalDeposits: number
  irmTotalDebt: number
  intrinsicYield: number
  rewards: any[]
  decimals: number
  config: { [key: string]: LenderConfigData }
  collateralActive: boolean
  borrowingEnabled: boolean
  /**
   * Whether this asset can currently be supplied to this spoke. Pendant to
   * `borrowingEnabled`. Sourced from `ReserveConfig.receiveSharesEnabled`
   * and gated by the spoke's `paused` flag — borrow-only spokes (e.g.
   * `TreasurySpoke`) will report `false` here while still reporting
   * `borrowingEnabled: true`.
   *
   * Also gated by the Hub-side spoke binding `active` flag: if the binding
   * is inactive, Hub.add() will revert regardless of spoke-level flags.
   */
  depositsEnabled: boolean
  hasStable: boolean
  isActive: boolean
  isFrozen: boolean
  borrowCap: number
  supplyCap: number
  debtCeiling: number
  /**
   * Max fraction of the debt repayable in one liquidation.
   *
   * **V4 has no close factor**, so this is a CEILING, not a rule: a liquidator
   * repays whatever restores the position to `targetHealthFactor`, which for a
   * deep enough drop is the whole debt. It used to emit V3's `0.5` as a
   * "cross-lender shape default" — but 0.5 is a real 50 % cap on V3 and a
   * statement about V4 that is simply false, and it travelled all the way to
   * the published term sheet as one. An unknown or inapplicable knob must not
   * borrow another protocol's number.
   */
  closeFactor: number
  /**
   * Spoke-level liquidation target health factor (e.g. 1.05). V4 liquidators
   * repay only enough debt to restore the position to this HF — the real
   * liquidation knob here, surfaced alongside the shape-default closeFactor.
   */
  targetHealthFactor?: number
  /**
   * Remaining deposit capacity for this spoke in token units.
   * Mirrors `TokenizationSpoke.maxDeposit()`:
   *   `max(0, addCap * 10^decimals - totalDeposits)`.
   * `null` means uncapped (`addCap == MAX_ALLOWED_SPOKE_CAP`).
   * `0` means no deposits are currently allowed (binding blocked, or cap
   * reached, or `addCap == 0`).
   */
  depositable: number | null
  /**
   * Hub-side borrow liquidity available for this spoke in token units.
   * Mirrors the borrow-liquidity computation from `normalize.ts`
   * (`min(hubLiquidity, drawCap headroom)`).
   * `null` means uncapped.
   */
  borrowLiquidity: number | null
  borrowLiquidityUSD: number
  /**
   * Hub-side spoke binding state. `true` when the spoke can interact with
   * the Hub for this asset. `false` blocks ALL Hub operations (draw, add,
   * remove, restore). Undefined when binding was not fetched.
   */
  spokeActive?: boolean
  /**
   * Hub-side spoke binding halt flag. When `true`, the spoke cannot perform
   * operations that instantly update liquidity (draw, add, remove, restore),
   * but can still report deficit. Undefined when binding was not fetched.
   */
  spokeHalted?: boolean
  /**
   * Per-marketUid identity. The lender-key-wide spoke/hub/oracle live on
   * the response wrapper's `params`, not here.
   */
  params: {
    metadata: AaveV4MarketMetadata
  }
  /**
   * Risk premium fields stamped directly on the entry (the entry IS one
   * spoke's view of one asset, so these are per-(spoke, asset) by
   * construction — no need for a wrapper).
   */
  collateralRiskBps: number
  riskPremiumThresholdBps: number
}

/**
 * One V4 spoke's normalized response. The shape mirrors what other
 * normalizers return (`{ data, chainId }`) plus a top-level `params` that
 * carries the lender-key-wide spoke/hub/oracle addresses.
 */
export interface AaveV4SpokeResponse {
  data: { [marketUid: string]: AaveV4TokenEntry }
  chainId: string
  params: AaveV4SpokeParams
}

/**
 * V4 is single-market per call now: each per-spoke synthesized lender key
 * (`AAVE_V4_<HEX>`) corresponds to exactly one fetcher call that returns
 * one `AaveV4SpokeResponse`. The dispatcher in `fetchLender.ts` stores it
 * under `lenderData[lender]` directly — same code path as Aave V3.
 */
export type NormalizedAaveV4Response = AaveV4SpokeResponse | undefined

// ---- Helpers ----

/**
 * Lossless bigint → decimal string converter for token amounts. Handles
 * values that overflow `Number.MAX_SAFE_INTEGER` (any 18-decimal token
 * worth more than ~9 units would lose precision via float division).
 */
function toTokenAmount(raw: bigint, decimals: number): string {
  if (raw === 0n) return '0'
  const negative = raw < 0n
  const abs = negative ? -raw : raw
  const s = abs.toString()
  let intPart: string
  let fracPart: string
  if (s.length <= decimals) {
    intPart = '0'
    fracPart = '0'.repeat(decimals - s.length) + s
  } else {
    intPart = s.slice(0, s.length - decimals)
    fracPart = s.slice(s.length - decimals)
  }
  fracPart = fracPart.replace(/0+$/, '')
  const out = fracPart ? `${intPart}.${fracPart}` : intPart
  return negative ? `-${out}` : out
}

/**
 * Lossy bigint → float conversion for intermediate computations (USD calcs,
 * headroom comparisons). Uses the lossless string path and `parseFloat` to
 * avoid the `Number(bigint)` overflow at 2^53 — this preserves full
 * significant-digit accuracy of the decimal representation rather than
 * truncating the integer mantissa.
 */
function toTokenNumber(raw: bigint, decimals: number): number {
  if (raw === 0n) return 0
  return parseFloat(toTokenAmount(raw, decimals))
}

/** RAY = 1e27 in Aave V4 (drawnRate is expressed in RAY) */
const RAY = 1e27

/**
 * `MAX_ALLOWED_SPOKE_CAP == type(uint40).max` — the on-chain sentinel that
 * `IHub.getSpoke(...).addCap` / `.drawCap` use to mean "uncapped".
 *
 * On-chain, `0` means **zero cap** (no draws / adds allowed):
 * `Hub._validateDraw` checks `drawCap * 10^d >= owed + amount + deficit`,
 * which always reverts when `drawCap == 0` for any positive amount.
 * `HubConfigurator.resetSpokeCaps` explicitly sets caps to 0 to revoke
 * spoke access.
 *
 * When `spokeBinding` is undefined (not fetched), caps default to 0 via
 * `?? 0` — in that case we treat 0 as "binding unknown / no constraint"
 * rather than "hard blocked", because we simply don't have the data.
 */
const MAX_SPOKE_CAP = 2 ** 40 - 1

const RAY_BIGINT = 10n ** 27n

/**
 * Convert a RAY-scaled annualized rate to APR percentage.
 *
 * The Hub's `drawnRate` (returned by `getAssetDrawnRate(assetId)`) is the
 * annualized borrow rate scaled by RAY — i.e. `drawnRate / RAY` is the APR
 * as a fraction (0.05 = 5%). It is NOT a per-second compounding factor.
 */
function rayRateToApr(rateRay: bigint): number {
  if (rateRay === 0n) return 0
  return (Number(rateRay) / RAY) * 100
}

// ---- Main normalization ----

export function normalizeAaveV4(
  spokeDataList: ParsedSpokeData[],
  chainId: string,
  /**
   * Per-spoke lender key (`AAVE_V4_<HEX>`). Pass-through; the lender-key
   * generator was already invoked by the caller — we use the value as the
   * top-level key under which to emit this spoke's response.
   */
  spokeLenderKeyArg: string,
  prices: { [asset: string]: number },
  additionalYields: any,
  tokenList: any,
  lenderShortNameFn: (l: string) => string,
  createMarketUidFn: (
    chainId: string,
    lender: string,
    refAddress: string,
  ) => string,
  toOracleKeyFn: (a: string | null) => string,
  toGenericPriceKeyFn: (asset: string, chainId: string) => string,
): NormalizedAaveV4Response {
  // Single-market: exactly one spoke per call. We accept a list for shape
  // parity with other normalizers but only honour the first entry.
  const spokeData = spokeDataList[0]
  if (!spokeData) return undefined

  {
    const spokeAddr = spokeData.spoke.toLowerCase()
    // The lender key passed in is already canonical for this spoke. We
    // re-derive defensively if the parsed key disagrees with the actual
    // spoke address (shouldn't happen unless the caller routed a stale
    // lender key — guard for misuse).
    const parsedFromArg = parseAaveV4SpokeLenderKey(spokeLenderKeyArg)
    const spokeLenderKey =
      parsedFromArg && parsedFromArg.spokeAddrLower === spokeAddr
        ? spokeLenderKeyArg
        : aaveV4SpokeLenderKey(spokeAddr)

    // Build per-reserve entries. Each reserve becomes one marketUid under
    // this spoke's per-spoke lender key. Hub identity is per-reserve and
    // travels in `params.metadata.hub`, not at the spoke level.
    const spokeReserves = spokeData.reserves
    const data: { [marketUid: string]: AaveV4TokenEntry } = {}

    for (const reserve of spokeReserves) {
      const reserveHub = (reserve.reserve.hub ?? '').toLowerCase()
      const underlying = reserve.reserve.underlying.toLowerCase()
      const decimals = reserve.reserve.decimals

      // MarketUid leaf is the **reserveId**, not the underlying. A spoke
      // can list the same underlying twice via different hubs, so reserveId
      // is the only stable per-(spoke, reserve) identifier. Underlying
      // remains in the entry body as `underlying` for display/lookup.
      // Format: `AAVE_V4_0x<spoke>:<chainId>:<reserveId>`
      const marketUid = createMarketUidFn(
        chainId,
        spokeLenderKey,
        String(reserve.reserveId),
      )

      const assetMeta = tokenList[underlying]

      // Price lookup
      const oracleKey =
        toOracleKeyFn(assetMeta?.assetGroup ?? null) ??
        toGenericPriceKeyFn(underlying, chainId)

      // Use on-chain oracle price if available, fall back to price feed
      let price = 0
      if (reserve.price > 0n) {
        price = Number(reserve.price) / 10 ** spokeData.oracleDecimals
      } else {
        price = prices[oracleKey] ?? 0
      }

      // Per-spoke token-denominated amounts.
      const spokeTotalDebtNum = toTokenNumber(reserve.totalDebt, decimals)

      // Per-spoke caps from the spoke binding (Hub.getSpoke). Whole tokens.
      const supplyCap = reserve.spokeBinding?.addCap ?? 0
      const borrowCap = reserve.spokeBinding?.drawCap ?? 0

      // Hub-side spoke binding state (per assetId × spoke).
      const bindingExists = reserve.spokeBinding !== undefined
      const spokeActive = reserve.spokeBinding?.active
      const spokeHalted = reserve.spokeBinding?.halted

      // Decimals on the hub side (used by both the borrow-liquidity and
      // the supply-rate utilization computations below).
      const hubDecimals = reserve.hubAsset.decimals || decimals

      // Per-spoke deficit from the binding, converted from RAY to tokens.
      // On-chain: `drawCap * 10^d >= owed + amount + ceil(deficitRay/RAY)`.
      // Match the on-chain `fromRayUp` (ceil) and stay in bigint to avoid
      // losing precision for large deficits (Number(bigint) overflows at 2^53).
      let spokeDeficitTokens = 0
      const rawDeficitRay = reserve.spokeBinding?.deficitRay
      if (rawDeficitRay && rawDeficitRay > 0n) {
        const deficitRawTokens = (rawDeficitRay + RAY_BIGINT - 1n) / RAY_BIGINT
        spokeDeficitTokens = toTokenNumber(deficitRawTokens, hubDecimals)
      }

      // ── Borrow liquidity (V4 hub-side semantics) ─────────────────────
      //
      // In V4, borrow liquidity is NOT `localDeposits - localDebt`. The
      // hub is the single source of truth: see Hub.draw at Hub.sol:249-271.
      // The maximum a spoke can borrow is:
      //
      //   min(
      //     hub._assets[A].liquidity,                                   // global pool
      //     drawCap × 10^decimals - spokeOwed - ceil(spokeDeficit/RAY)  // per-spoke headroom
      //   )
      //
      // Three gating layers must ALL pass for a draw to succeed:
      //   1. Hub binding: spoke.active && !spoke.halted
      //   2. Draw cap: 0 = blocked, MAX_SPOKE_CAP = uncapped, else real cap
      //   3. Hub liquidity: amount <= asset.liquidity
      const hubLiquidityNum = toTokenNumber(
        reserve.hubAsset.liquidity ?? 0n,
        hubDecimals,
      )
      let borrowLiquidityNum = hubLiquidityNum

      // Layer 1: Hub binding active/halted gate.
      // On-chain: _validateDraw requires spoke.active && !spoke.halted.
      const hubBindingBlocked =
        bindingExists && (spokeActive === false || spokeHalted === true)

      if (hubBindingBlocked) {
        borrowLiquidityNum = 0
      } else if (bindingExists && borrowCap === 0) {
        // Layer 2a: drawCap == 0 on-chain means zero draws allowed.
        // HubConfigurator.resetSpokeCaps sets caps to 0 to revoke access.
        borrowLiquidityNum = 0
      } else if (borrowCap > 0 && borrowCap < MAX_SPOKE_CAP) {
        // Layer 2b: real draw cap — apply headroom with deficit.
        const spokeHeadroom = borrowCap - spokeTotalDebtNum - spokeDeficitTokens
        if (spokeHeadroom < borrowLiquidityNum)
          borrowLiquidityNum = spokeHeadroom
      }
      // else: borrowCap >= MAX_SPOKE_CAP or binding unknown → uncapped

      if (borrowLiquidityNum < 0) borrowLiquidityNum = 0

      // Interest rates from Hub asset.
      // drawnRate is the annualized borrow rate in RAY.
      const variableBorrowRate = rayRateToApr(reserve.hubAsset.drawnRate)

      // ── Hub-level aggregates (IRM-relevant) ──────────────────────────
      //
      // The V4 IRM lives on the hub and aggregates drawn liquidity across
      // **all** spokes sharing this asset. These hub totals are the
      // canonical IRM input and live in `hubDeposits` / `hubBorrows`.
      // `totalDeposits` / `totalDebt` carry spoke-local values so that
      // `Σ totalDeposits − Σ totalDebt` across spokes gives correct TVL
      // without double-counting the shared hub pool.
      const hubTotalSupplyRaw = reserve.hubAsset.totalAddedAssets ?? 0n
      const hubTotalOwedRaw = reserve.hubAsset.totalOwedAssets ?? 0n
      const hubDepositsStr = toTokenAmount(hubTotalSupplyRaw, hubDecimals)
      const hubBorrowsStr = toTokenAmount(hubTotalOwedRaw, hubDecimals)
      const hubTotalSupply = toTokenNumber(hubTotalSupplyRaw, hubDecimals)
      const hubTotalOwed = toTokenNumber(hubTotalOwedRaw, hubDecimals)
      const hubDepositsUSD = hubTotalSupply * price
      const hubBorrowsUSD = hubTotalOwed * price

      // Exact IRM input. The hub's AssetInterestRateStrategy computes
      //   usageRatio = drawn / (liquidity + drawn + swept)
      // where drawn = drawnShares × drawnIndex (premium debt excluded,
      // deficit ignored — verified against the deployed HubInstance +
      // strategy source). `owed/added` differs by realized fees and premium
      // debt, so curve(owed/added) would not land exactly on `drawnRate`.
      const drawnRaw =
        ((reserve.hubAsset.drawnShares ?? 0n) *
          (reserve.hubAsset.drawnIndex ?? 0n)) /
        RAY_BIGINT
      const irmDenominatorRaw =
        (reserve.hubAsset.liquidity ?? 0n) +
        drawnRaw +
        (reserve.hubAsset.swept ?? 0n)
      let irmTotalDebt = toTokenNumber(drawnRaw, hubDecimals)
      let irmTotalDeposits = toTokenNumber(irmDenominatorRaw, hubDecimals)
      if (drawnRaw === 0n && hubTotalOwed > 0) {
        // Drawn-share data missing while owed > 0 (partial getAsset decode)
        // — fall back to the owed/added approximation rather than a bogus
        // zero utilization.
        irmTotalDebt = hubTotalOwed
        irmTotalDeposits = hubTotalSupply
      }
      const hubUtilization =
        irmTotalDeposits > 0 ? irmTotalDebt / irmTotalDeposits : 0
      const feeRate = reserve.hubAsset.liquidityFee
        ? reserve.hubAsset.liquidityFee / BPS
        : 0
      const depositRate = variableBorrowRate * hubUtilization * (1 - feeRate)

      // Risk premium per (spoke, asset)
      const collateralRiskBps =
        reserve.config.collateralRisk || reserve.reserve.collateralRisk || 0
      const riskPremiumThresholdBps =
        reserve.spokeBinding?.riskPremiumThreshold ?? 0

      // Spoke-level liquidation target health factor (WAD, 1e18 == 1.00 per
      // README). V4 liquidators repay only enough to restore the position to
      // this HF — the real liquidation knob (no fixed close factor).
      const targetHealthFactorRaw =
        spokeData.liquidationConfig?.targetHealthFactor ?? 0n
      const targetHealthFactor =
        targetHealthFactorRaw > 0n
          ? Number(targetHealthFactorRaw) / 1e18
          : undefined

      // Build config map keyed by dynamicConfigKey (no spoke prefix — the
      // spoke is already the lender key). Mirrors V3's e-mode shape.
      const config: { [key: string]: LenderConfigData } = {}
      for (const [keyStr, dynCfg] of Object.entries(reserve.dynamicConfigs)) {
        config[keyStr] = {
          category: Number(keyStr),
          label: `${spokeData.label} / Config ${keyStr}`,
          borrowCollateralFactor: dynCfg.collateralFactor / BPS,
          collateralFactor: dynCfg.collateralFactor / BPS,
          borrowFactor: 1,
          // `maxLiquidationBonus` is in PERCENTAGE_FACTOR units (BPS) where
          // 10000 == par (0% bonus); the on-chain validator requires it to be
          // >= PERCENTAGE_FACTOR (README: "bonus ≥ 100% (= ≥ 0% bonus)"). Same
          // semantics as V3's `liquidationBonus`, so penalty = bps/BPS - 1.
          liquidationPenalty:
            Number.isFinite(dynCfg.maxLiquidationBonus) &&
            dynCfg.maxLiquidationBonus > 0
              ? dynCfg.maxLiquidationBonus / BPS - 1
              : 0,
          // No close factor in V4 — the ceiling is the whole debt. See the
          // field docstring on `AaveV4TokenEntry.closeFactor`.
          closeFactor: 1,
          targetHealthFactor,
          // _validateSetUsingAsCollateral: !paused && !frozen (frozen blocks enabling)
          collateralDisabled:
            dynCfg.collateralFactor === 0 ||
            reserve.config.paused ||
            reserve.config.frozen,
          // _validateBorrow: !paused && !frozen && borrowable
          debtDisabled:
            !reserve.config.borrowable ||
            reserve.config.paused ||
            reserve.config.frozen,
        }
      }

      // If no dynamic configs exist, add a default entry
      if (Object.keys(config).length === 0) {
        config['0'] = {
          category: 0,
          label: `${spokeData.label} / Default`,
          borrowCollateralFactor: 0,
          collateralFactor: 0,
          borrowFactor: 1,
          liquidationPenalty: 0,
          // No close factor in V4 — the ceiling is the whole debt. See the
          // field docstring on `AaveV4TokenEntry.closeFactor`.
          closeFactor: 1,
          targetHealthFactor,
          collateralDisabled: true,
          debtDisabled:
            !reserve.config.borrowable ||
            reserve.config.paused ||
            reserve.config.frozen,
        }
      }

      const collateralActive = Object.values(reserve.dynamicConfigs).some(
        (c) => c.collateralFactor > 0,
      )

      // Spoke-local supply/debt — these become the canonical totalDeposits /
      // totalDebt so that aggregating across spokes yields correct TVL.
      const totalDepositsStr = toTokenAmount(reserve.suppliedAssets, decimals)
      const totalDebtStr = toTokenAmount(reserve.totalDebt, decimals)
      const spokeSupplyNum = toTokenNumber(reserve.suppliedAssets, decimals)
      const totalDepositsUSD = spokeSupplyNum * price
      const totalDebtUSD = spokeTotalDebtNum * price

      // Deposit capacity
      // `addCap` is in whole tokens; `spokeSupplyNum` is already decimal-
      // scaled by `toTokenNumber`, so the subtraction gives headroom in
      // the same token units (e.g. 1.5 USDC = 1.5, not 1_500_000).
      let depositableNum: number | null
      if (hubBindingBlocked || (bindingExists && supplyCap === 0)) {
        // (!active || halted)        → 0
        depositableNum = 0
      } else if (supplyCap >= MAX_SPOKE_CAP) {
        // (addCap == MAX_SPOKE_CAP)  → null  (uncapped)
        depositableNum = null // uncapped
      } else {
        // max(0, addCap - spokeSupplyNum)
        depositableNum = Math.max(0, supplyCap - spokeSupplyNum)
      }

      data[marketUid] = {
        marketUid,
        name: 'Aave V4 ' + spokeData.label + ' ' + (assetMeta?.symbol ?? ''),
        poolId: underlying,
        underlying,
        asset: assetMeta,
        totalDeposits: totalDepositsStr,
        totalDebtStable: '0',
        totalDebt: totalDebtStr,
        // Raw hub liquidity — withdraw-side ceiling, not cap-gated.
        totalLiquidity: hubLiquidityNum,
        totalDepositsUSD,
        totalDebtStableUSD: 0,
        totalDebtUSD,
        totalLiquidityUSD: hubLiquidityNum * price,
        // Hub aggregates for IRM consumers.
        hubDeposits: hubDepositsStr,
        hubBorrows: hubBorrowsStr,
        hubDepositsUSD,
        hubBorrowsUSD,
        depositRate,
        variableBorrowRate,
        stableBorrowRate: 0,
        utilization: hubUtilization,
        // Totals in the hub IRM's own utilization basis — rate-impact
        // simulations must shift these, not the spoke-local totals.
        irmTotalDeposits,
        irmTotalDebt,
        intrinsicYield: additionalYields?.intrinsicYields?.[oracleKey] ?? 0,
        rewards: [],
        decimals,
        config,
        collateralActive,
        // On-chain: Spoke._validateBorrow checks !paused && !frozen && borrowable,
        // then Hub._validateDraw checks spoke.active && !halted && drawCap headroom.
        borrowingEnabled:
          reserve.config.borrowable &&
          !reserve.config.paused &&
          !reserve.config.frozen &&
          !hubBindingBlocked &&
          !(bindingExists && borrowCap === 0),
        // On-chain: Spoke._validateSupply checks !paused && !frozen,
        // then Hub._validateAdd checks spoke.active && !halted && addCap headroom.
        // Note: receiveSharesEnabled is a liquidation-mode flag (whether liquidators
        // receive shares or underlying) — it does NOT gate user deposits.
        depositsEnabled:
          !reserve.config.paused &&
          !reserve.config.frozen &&
          !hubBindingBlocked &&
          !(bindingExists && supplyCap === 0),
        hasStable: false,
        isActive: !reserve.config.paused,
        isFrozen: reserve.config.frozen,
        borrowCap,
        supplyCap,
        debtCeiling: 0,
        // No close factor in V4 — the ceiling is the whole debt.
        closeFactor: 1,
        targetHealthFactor,
        spokeActive,
        spokeHalted,
        params: {
          metadata: {
            assetId: reserve.reserve.assetId,
            reserveId: reserve.reserveId,
            hub: reserveHub,
          },
        },
        collateralRiskBps,
        riskPremiumThresholdBps,
        depositable: depositableNum,
        borrowLiquidity: borrowLiquidityNum,
        borrowLiquidityUSD: borrowLiquidityNum * price,
      }
    }

    if (Object.keys(data).length === 0) return undefined

    // Recomputed here rather than reusing the loop-local value: these are
    // spoke-level facts and the loop may not have run for any reserve.
    const liqCfg = spokeData.liquidationConfig
    const wad = (v: bigint | undefined) =>
      v != null && v > 0n ? Number(v) / 1e18 : undefined

    return {
      data,
      chainId,
      params: {
        spoke: spokeAddr,
        oracle: spokeData.oracle.toLowerCase(),
        label: spokeData.label,
        liquidation: {
          targetHealthFactor: wad(liqCfg?.targetHealthFactor),
          healthFactorForMaxBonus: wad(liqCfg?.healthFactorForMaxBonus),
          liquidationBonusFactorBps:
            liqCfg?.liquidationBonusFactor || undefined,
        },
      },
    }
  }
}
